import { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  BarChart3, FileSpreadsheet, Calculator, TrendingUp,
  Brain, FileDown, Upload, ChevronLeft, ChevronRight,
  LayoutDashboard, Activity, Shield, Gauge, Share2, GitCompare, Wallet, HelpCircle, ClipboardCheck,
  Users, LogOut, User, Lock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, stage: null },
  { id: 'import', label: 'Import Data', icon: Upload, stage: null, writeRequired: true },
  { id: 'deal-comparison', label: 'Deal Comparison', icon: BarChart3, stage: 'Stage 1' },
  { id: 'sde', label: 'SDE Calculator', icon: Calculator, stage: 'Stage 1' },
  { id: 'pnl', label: 'P&L Summary', icon: FileSpreadsheet, stage: 'Stage 1' },
  { id: 'solvency', label: 'Solvency', icon: Shield, stage: 'Stage 1' },
  { id: 'inventory', label: 'Inventory', icon: Activity, stage: 'Stage 1' },
  { id: 'working-capital', label: 'Working Capital', icon: Wallet, stage: 'Stage 1' },
  { id: 'dcf', label: 'DCF Valuation', icon: TrendingUp, stage: 'Stage 2' },
  { id: 'scenarios', label: 'Scenario Analysis', icon: BarChart3, stage: 'Stage 2' },
  { id: 'sensitivity', label: 'Sensitivity', icon: Gauge, stage: 'Stage 2' },
  { id: 'findings', label: 'Key Findings', icon: Brain, stage: null },
  { id: 'compare', label: 'Compare Deals', icon: GitCompare, stage: null },
  { id: 'validation', label: 'Model Check', icon: ClipboardCheck, stage: null, writeRequired: true },
  { id: 'report', label: 'Generate Report', icon: FileDown, stage: null },
];

const bottomNavItems = [
  { id: 'guide', label: 'User Guide', icon: HelpCircle },
];

const adminNavItems = [
  { id: 'users', label: 'User Management', icon: Users },
];

// Items accessible with step1_only access
const step1Items = ['dashboard', 'deal-comparison', 'sde', 'pnl', 'solvency', 'inventory', 'working-capital'];

export default function Layout({ activeTab, onTabChange, analysisName, children, user, onLogout, isAdmin, userAccess, onGoHome }) {
  const [collapsed, setCollapsed] = useState(false);
  
  // Determine which items to show based on access level
  const getFilteredNavItems = () => {
    // Admins and owners see everything
    if (isAdmin || userAccess?.is_owner) {
      return navItems;
    }
    
    // Clients with step1_only access
    if (userAccess?.access_level === 'step1_only') {
      return navItems.filter(item => step1Items.includes(item.id));
    }
    
    // Clients with full_access (but read-only) - hide import and validation
    if (userAccess?.access_level === 'full_access') {
      return navItems.filter(item => !item.writeRequired);
    }
    
    // Default - show all for now
    return navItems;
  };
  
  const filteredNavItems = getFilteredNavItems();
  const isReadOnly = userAccess?.read_only;

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-screen overflow-hidden noise-overlay">
        {/* Sidebar */}
        <aside
          className={cn(
            "flex flex-col border-r border-white/[0.06] bg-[#0c0c0f] transition-all duration-300 relative z-10 shrink-0",
            collapsed ? "w-16" : "w-56"
          )}
          data-testid="sidebar"
        >
          {/* Logo - Clickable to go to Dashboard/Home */}
          <button
            onClick={() => onGoHome ? onGoHome() : onTabChange('dashboard')}
            className="flex items-center gap-2 px-4 h-14 border-b border-white/[0.06] shrink-0 w-full hover:bg-white/[0.02] transition-colors cursor-pointer"
            data-testid="logo-home"
            title="Go to Dashboard"
          >
            <div className="w-7 h-7 rounded bg-indigo-500/20 flex items-center justify-center shrink-0">
              <BarChart3 className="w-4 h-4 text-indigo-400" />
            </div>
            {!collapsed && (
              <span className="text-sm font-semibold tracking-tight text-white font-['Manrope']">
                AcquiFlow
              </span>
            )}
          </button>

          {/* Nav */}
          <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
            {filteredNavItems.map((item) => {
              const stage = item.stage;
              const isActive = activeTab === item.id;
              const Icon = item.icon;

              const btn = (
                <button
                  key={item.id}
                  data-testid={`nav-${item.id}`}
                  onClick={() => onTabChange(item.id)}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-all duration-200",
                    isActive
                      ? "bg-indigo-500/15 text-indigo-300 border border-indigo-500/30"
                      : "text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.04] border border-transparent"
                  )}
                >
                  <Icon className={cn("w-4 h-4 shrink-0", isActive && "text-indigo-400")} />
                  {!collapsed && (
                    <span className="flex-1 text-left truncate">{item.label}</span>
                  )}
                  {!collapsed && stage && (
                    <span className={cn(
                      "text-[10px] px-1.5 py-0.5 rounded font-mono",
                      stage === 'Stage 1' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'
                    )}>
                      {stage}
                    </span>
                  )}
                </button>
              );

              if (collapsed) {
                return (
                  <Tooltip key={item.id}>
                    <TooltipTrigger asChild>{btn}</TooltipTrigger>
                    <TooltipContent side="right" className="bg-zinc-900 border-zinc-800 text-zinc-200">
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                );
              }
              return btn;
            })}
          </nav>

          {/* Bottom Nav - User Guide */}
          <div className="px-2 py-2 border-t border-white/[0.06]">
            {bottomNavItems.map((item) => {
              const isActive = activeTab === item.id;
              const Icon = item.icon;

              const btn = (
                <button
                  key={item.id}
                  data-testid={`nav-${item.id}`}
                  onClick={() => onTabChange(item.id)}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-all duration-200",
                    isActive
                      ? "bg-indigo-500/15 text-indigo-300 border border-indigo-500/30"
                      : "text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.04] border border-transparent"
                  )}
                >
                  <Icon className={cn("w-4 h-4 shrink-0", isActive && "text-indigo-400")} />
                  {!collapsed && (
                    <span className="flex-1 text-left truncate">{item.label}</span>
                  )}
                </button>
              );

              if (collapsed) {
                return (
                  <Tooltip key={item.id}>
                    <TooltipTrigger asChild>{btn}</TooltipTrigger>
                    <TooltipContent side="right" className="bg-zinc-900 border-zinc-800 text-zinc-200">
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                );
              }
              return btn;
            })}
          </div>

          {/* Admin Nav Items (Users) */}
          {isAdmin && (
            <div className="px-2 py-2 border-t border-white/[0.06]">
              {adminNavItems.map((item) => {
                const isActive = activeTab === item.id;
                const Icon = item.icon;
                const btn = (
                  <button
                    key={item.id}
                    data-testid={`nav-${item.id}`}
                    onClick={() => onTabChange(item.id)}
                    className={cn(
                      "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-all duration-200",
                      isActive
                        ? "bg-indigo-500/15 text-indigo-300 border border-indigo-500/30"
                        : "text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.04] border border-transparent"
                    )}
                  >
                    <Icon className={cn("w-4 h-4 shrink-0", isActive && "text-indigo-400")} />
                    {!collapsed && <span className="flex-1 text-left truncate">{item.label}</span>}
                  </button>
                );
                if (collapsed) {
                  return (
                    <Tooltip key={item.id}>
                      <TooltipTrigger asChild>{btn}</TooltipTrigger>
                      <TooltipContent side="right" className="bg-zinc-900 border-zinc-800 text-zinc-200">
                        {item.label}
                      </TooltipContent>
                    </Tooltip>
                  );
                }
                return btn;
              })}
            </div>
          )}

          {/* User Info & Logout */}
          {user && (
            <div className="px-2 py-2 border-t border-white/[0.06]">
              <div className={cn(
                "flex items-center gap-2 px-2.5 py-2 rounded-md",
                collapsed ? "justify-center" : ""
              )}>
                <div className={cn(
                  "w-7 h-7 rounded-full flex items-center justify-center shrink-0",
                  user.role === 'admin' ? 'bg-indigo-500/20' : 'bg-zinc-700/50'
                )}>
                  <User className={cn("w-3.5 h-3.5", user.role === 'admin' ? 'text-indigo-400' : 'text-zinc-400')} />
                </div>
                {!collapsed && (
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-zinc-200 truncate">{user.name}</div>
                    <div className="text-[10px] text-zinc-500">{user.role}</div>
                  </div>
                )}
                {!collapsed && onLogout && (
                  <button
                    onClick={onLogout}
                    className="text-zinc-500 hover:text-red-400 transition-colors"
                    title="Logout"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                )}
              </div>
              {collapsed && onLogout && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={onLogout}
                      className="w-full flex items-center justify-center py-2 text-zinc-500 hover:text-red-400 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-zinc-900 border-zinc-800 text-zinc-200">
                    Logout
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          )}

          {/* Collapse Toggle */}
          <div className="px-2 py-2 border-t border-white/[0.06]">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCollapsed(!collapsed)}
              className="w-full justify-center text-zinc-500 hover:text-zinc-300"
              data-testid="toggle-sidebar"
            >
              {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </Button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-hidden flex flex-col">
          {/* Top bar */}
          <header className="h-14 border-b border-white/[0.06] flex items-center justify-between px-6 bg-[#09090b]/80 backdrop-blur-sm shrink-0">
            <div className="flex items-center gap-3">
              <h1 className="text-sm font-semibold text-zinc-200 font-['Manrope']">
                {analysisName || 'Business Acquisition Analysis'}
              </h1>
              {filteredNavItems.find(i => i.id === activeTab)?.stage && (
                <span className={cn(
                  "text-[10px] px-2 py-0.5 rounded font-mono font-medium",
                  filteredNavItems.find(i => i.id === activeTab)?.stage === 'Stage 1' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'
                )}>
                  {filteredNavItems.find(i => i.id === activeTab)?.stage}
                </span>
              )}
              {isReadOnly && (
                <span className="text-[10px] px-2 py-0.5 rounded font-mono font-medium bg-zinc-700/50 text-zinc-400 flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Read Only
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-zinc-500 font-mono">
              {filteredNavItems.find(i => i.id === activeTab)?.label}
            </div>
          </header>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-[1600px] mx-auto p-6">
              {children}
            </div>
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}
